import { useState, useCallback, useEffect } from 'react';
import { FileStatus } from 'Enum';

export interface IUploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadProgress: number;
  status: FileStatus;
  errorMessage?: string;
  file?: File;
}

export interface IDocumentType {
  id: string;
  type: string;
  label: string;
  files: IUploadedFile[];
  isOpen: boolean;
}

export interface IDocumentTypeOption {
  type: string;
  label: string;
}

interface IUseDocumentUpload {
  documentTypes: IDocumentType[];
  addDocumentType: (type: IDocumentTypeOption) => void;
  addDocumentTypeWithFiles: (
    type: IDocumentTypeOption,
    files: IUploadedFile[]
  ) => void;
  removeDocumentType: (typeId: string) => void;
  toggleCollapse: (typeId: string) => void;
  addFile: (typeId: string, file: IUploadedFile) => void;
  removeFile: (typeId: string, fileId: string) => void;
  updateFileProgress: (
    typeId: string,
    fileId: string,
    progress: number
  ) => void;
  updateFileStatus: (
    typeId: string,
    fileId: string,
    status: FileStatus,
    errorMessage?: string
  ) => void;
  getAvailableTypes: (allTypes: IDocumentTypeOption[]) => IDocumentTypeOption[];
  getTotalFiles: () => number;
  getFilesByStatus: (status: FileStatus) => number;
  clearAll: () => void;
}

export const OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS = ['77', '74', '185'];

export const useDocumentUpload = (
  initialTypes: IDocumentType[] = [],
  onChange?: (documentTypes: IDocumentType[]) => void
): IUseDocumentUpload => {
  const [documentTypes, setDocumentTypes] =
    useState<IDocumentType[]>(initialTypes);

  const updateAndNotify = useCallback(
    (updater: (prev: IDocumentType[]) => IDocumentType[]) => {
      setDocumentTypes((prev) => updater(prev));
    },
    []
  );

  useEffect(() => {
    onChange?.(documentTypes);
  }, [documentTypes, onChange]);

  const addDocumentType = useCallback(
    (type: IDocumentTypeOption) => {
      updateAndNotify((prev) => {
        const updatedTypes = prev.map((dt) => ({ ...dt, isOpen: false }));

        const newType: IDocumentType = {
          id: `${Date.now()}-${Math.random()}`,
          type: type.type,
          label: type.label,
          files: [],
          isOpen: true,
        };

        return [...updatedTypes, newType];
      });
    },
    [updateAndNotify]
  );

  const addDocumentTypeWithFiles = useCallback(
    (type: IDocumentTypeOption, files: IUploadedFile[]) => {
      updateAndNotify((prev) => {
        const updatedTypes = prev.map((dt) => ({ ...dt, isOpen: false }));

        const newType: IDocumentType = {
          id: `${Date.now()}-${Math.random()}`,
          type: type.type,
          label: type.label,
          files: [...files],
          isOpen: true,
        };

        return [...updatedTypes, newType];
      });
    },
    [updateAndNotify]
  );

  const removeDocumentType = useCallback(
    (typeId: string) => {
      updateAndNotify((prev) => prev.filter((dt) => dt.id !== typeId));
    },
    [updateAndNotify]
  );

  const toggleCollapse = useCallback(
    (typeId: string) => {
      updateAndNotify((prev) =>
        prev.map((dt) => ({
          ...dt,
          isOpen: dt.id === typeId ? !dt.isOpen : false,
        }))
      );
    },
    [updateAndNotify]
  );

  const addFile = useCallback(
    (typeId: string, file: IUploadedFile) => {
      updateAndNotify((prev) =>
        prev.map((dt) =>
          dt.id === typeId
            ? {
                ...dt,
                files: [...dt.files, file],
              }
            : dt
        )
      );
    },
    [updateAndNotify]
  );

  const removeFile = useCallback(
    (typeId: string, fileId: string) => {
      updateAndNotify((prev) =>
        prev.map((dt) =>
          dt.id === typeId
            ? {
                ...dt,
                files: dt.files.filter((f) => f.id !== fileId),
              }
            : dt
        )
      );
    },
    [updateAndNotify]
  );

  const updateFileProgress = useCallback(
    (typeId: string, fileId: string, progress: number) => {
      updateAndNotify((prev) =>
        prev.map((dt) =>
          dt.id === typeId
            ? {
                ...dt,
                files: dt.files.map((f) =>
                  f.id === fileId ? { ...f, uploadProgress: progress } : f
                ),
              }
            : dt
        )
      );
    },
    [updateAndNotify]
  );

  const updateFileStatus = useCallback(
    (
      typeId: string,
      fileId: string,
      status: FileStatus,
      errorMessage?: string
    ) => {
      updateAndNotify((prev) =>
        prev.map((dt) =>
          dt.id === typeId
            ? {
                ...dt,
                files: dt.files.map((f) =>
                  f.id === fileId ? { ...f, status, errorMessage } : f
                ),
              }
            : dt
        )
      );
    },
    [updateAndNotify]
  );

  const getAvailableTypes = useCallback(
    (allTypes: IDocumentTypeOption[]): IDocumentTypeOption[] => {
      const selectedTypes = documentTypes.map((dt) => dt.type);
      const filtered = allTypes.filter(
        (type) => !selectedTypes.includes(type.type)
      );
      return [...filtered].sort((a, b) => {
        const aIsOther = OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS.includes(
          String(a.type)
        );
        const bIsOther = OTHER_ATTACHMENT_DOCUMENT_TYPE_IDS.includes(
          String(b.type)
        );
        if (aIsOther !== bIsOther) {
          return aIsOther ? 1 : -1;
        }
        return 0;
      });
    },
    [documentTypes]
  );

  const getTotalFiles = useCallback(() => {
    return documentTypes.reduce((sum, dt) => sum + dt.files.length, 0);
  }, [documentTypes]);

  const getFilesByStatus = useCallback(
    (status: FileStatus) => {
      return documentTypes.reduce(
        (sum, dt) => sum + dt.files.filter((f) => f.status === status).length,
        0
      );
    },
    [documentTypes]
  );

  const clearAll = useCallback(() => {
    updateAndNotify(() => []);
  }, [updateAndNotify]);

  return {
    documentTypes,
    addDocumentType,
    addDocumentTypeWithFiles,
    removeDocumentType,
    toggleCollapse,
    addFile,
    removeFile,
    updateFileProgress,
    updateFileStatus,
    getAvailableTypes,
    getTotalFiles,
    getFilesByStatus,
    clearAll,
  };
};
